import { cx } from "class-variance-authority";
import { ReactNode } from "react";
import { ErrorIndicator } from "./indicators/error-indicator";
import { LoadingIndicator } from "./indicators/loading-indicator";
import { IndicatorSize } from "./indicators/types";

const sharedClassNames = cx(
  "absolute top-0 left-0",
  "w-full h-full",
  "justify-center items-center",
);

interface LoadingMaskProps {
  loading:
    | boolean
    | {
        isLoading: boolean;
        message: string;
      };
  error?: {
    error: unknown;
    message: string;
    mask?: ReactNode;
  };
  children?: ReactNode;
  /**
   * Style the outer `<div>`.
   */
  className?: string;
  /**
   * Style the inner `<div>`.
   */
  innerClassName?: string;
  indicatorSize?: IndicatorSize;
}
/**
 * When loading, a `<div>` equal to the size of the loading children, with a centered loading spinner and optional message.
 */
export const LoadingMask = ({
  indicatorSize = 6,
  ...props
}: LoadingMaskProps) => {
  let normalizedLoading =
    typeof props.loading === "boolean"
      ? { isLoading: props.loading, message: "" }
      : props.loading;

  const showError = !!props?.error?.error;
  const showLoading = normalizedLoading.isLoading && !showError;
  const hideContent = showLoading || showError;

  const errorMask = props?.error?.mask ?? (
    <ErrorIndicator
      message={props?.error?.message ?? ""}
      indicatorSize={indicatorSize}
      className={sharedClassNames}
    />
  );

  return (
    <div
      className={cx(
        hideContent ? "relative" : "",
        props.className,
        "h-full w-full",
      )}
      aria-live="polite"
      aria-busy={normalizedLoading.isLoading}
    >
      <div
        className={cx(
          // 'invisible' removes semantics from content while preserving size and position; 'opacity-0' avoids bug where a 'react-select' dropdown lingers for an extra frame before going 'invisible'
          hideContent && "invisible opacity-0",
          "h-full w-full",
          props.innerClassName,
        )}
      >
        {props.children ?? <div className="h-screen w-screen" />}
      </div>
      {showError && errorMask}
      {showLoading && (
        <LoadingIndicator
          message={normalizedLoading.message}
          indicatorSize={indicatorSize}
          className={sharedClassNames}
        />
      )}
    </div>
  );
};

export const LoadingWrapper = (props: {
  loading?: boolean;
  loadingMessage?: string;
  error?: boolean;
  errorMessage?: string;
  children?: ReactNode;
}) => {
  const message = props.loadingMessage ?? "Loading...";
  return (
    <LoadingMask
      error={{
        error: props.error ?? false,
        message: props.errorMessage ?? "Error",
      }}
      loading={{
        isLoading: props.loading,
        message,
      }}
    >
      {props.children}
    </LoadingMask>
  );
};
